import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { hasSelectedIntervention } from 'components/Assessments/utils/interventions'
import { PlanOfCareFormField } from './formField'
import { AdapterOptions, FlatAnswerMutation } from '../../../adapters/types'
import { RecertFormData } from '../../types/RecertFormData'
import { RecertAssessmentQuestionnaireItem } from '../../types/questionnaire'
import { createAnswerItemsFromValues, getAnswerValuesFromItems } from '../../../helpers'
import { SharedCareQuestionnaireItem } from '../../../hooks/constants'
import { SectionAdapter } from '../../../adapters/types'

/**
 * Plan of Care subsection adapter for Recert Assessment
 * Handles goals and interventions extraction and transformation
 */
export class PlanOfCareAdapter extends SectionAdapter<RecertFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options: AdapterOptions
  ): Partial<RecertFormData> {
    // Extract goals from flat answers
    const goals = getAnswerValuesFromItems(
      Object.values(flatAnswers),
      SharedCareQuestionnaireItem('PLAN_OF_CARE_GOALS_GOAL')
    )

    // Extract interventions from flat answers
    const interventionAnswers =
      flatAnswers[
        RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO
      ]?.answer ?? []
    const interventions = interventionAnswers?.map((interventionAnswerItem) => {
      const interventionInfoItem = interventionAnswerItem.item ?? []

      const interventionId = getAnswerValuesFromItems(
        interventionInfoItem,
        RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_INTERVENTION
      )?.at(0)

      const interventionCategory = getAnswerValuesFromItems(
        interventionInfoItem,
        RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_CATEGORY
      )?.at(0)

      const interventionGoal = getAnswerValuesFromItems(
        interventionInfoItem,
        RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_GOAL
      )?.at(0)

      const interventionTask = getAnswerValuesFromItems(
        interventionInfoItem,
        RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_TASK
      )?.at(0)

      const interventionIndication = getAnswerValuesFromItems(
        interventionInfoItem,
        RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_INDICATION
      )?.at(0)

      const interventionType = getAnswerValuesFromItems(
        interventionInfoItem,
        RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_TYPE
      )?.at(0)

      const interventionInstruction = getAnswerValuesFromItems(
        interventionInfoItem,
        RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_INSTRUCTION
      )?.at(0)

      const interventionPatientCenteredGoal = getAnswerValuesFromItems(
        interventionInfoItem,
        RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_PATIENT_CENTERED_GOAL
      )?.at(0)

      const interventionAdditionalDetails = getAnswerValuesFromItems(
        interventionInfoItem,
        RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_ADDITIONAL_DETAILS
      )?.at(0)

      const interventionVisits = getAnswerValuesFromItems(
        interventionInfoItem,
        RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_VISITS
      )

      const interventionFrequency = getAnswerValuesFromItems(
        interventionInfoItem,
        RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_FREQUENCY
      )

      return {
        id: interventionId,
        category: interventionCategory,
        interventionGoal: interventionGoal,
        task: interventionTask,
        indication: interventionIndication,
        type: interventionType,
        instruction: interventionInstruction,
        patientCenteredGoal: interventionPatientCenteredGoal,
        additionalDetails: interventionAdditionalDetails,
        visitNumbers: interventionVisits ?? [],
        frequency: interventionFrequency ?? []
      }
    })

    const planOfCareInitialsAuthorization = getAnswerValuesFromItems(
      Object.values(flatAnswers),
      RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_INITIALS_AUTHORIZATION
    )?.at(0)

    return {
      [PlanOfCareFormField.GOALS]: goals,
      [PlanOfCareFormField.INTERVENTIONS]: interventions ?? [],
      [PlanOfCareFormField.INITIALS_AUTHORIZATION]: planOfCareInitialsAuthorization
    }
  }

  buildFlatAnswers(formData: RecertFormData): FlatAnswerMutation[] {
    const planOfCareAnswers: FlatAnswerMutation[] = []

    // Build goals flat answers
    const goals = formData[PlanOfCareFormField.GOALS]
    if (goals) {
      planOfCareAnswers.push({
        linkId: RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_GOALS_GOAL,
        value: goals?.filter((goal) => goal)
      })
    }

    const interventions = formData[PlanOfCareFormField.INTERVENTIONS]
    // Build interventions flat answers
    if (interventions) {
      const interventionAnswers = interventions
        .filter(hasSelectedIntervention)
        .map((intervention) => ({
          item: createAnswerItemsFromValues([
            [
              RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_INTERVENTION,
              intervention.id
            ],
            [
              RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_CATEGORY,
              intervention.category
            ],
            [
              RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_GOAL,
              intervention.interventionGoal
            ],
            [
              RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_TASK,
              intervention.task
            ],
            [
              RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_INDICATION,
              intervention.indication
            ],
            [
              RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_TYPE,
              intervention.type
            ],
            [
              RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_INSTRUCTION,
              intervention.instruction
            ],
            [
              RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_PATIENT_CENTERED_GOAL,
              intervention.patientCenteredGoal
            ],
            [
              RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_ADDITIONAL_DETAILS,
              intervention.additionalDetails
            ],
            [
              RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_VISITS,
              intervention.visitNumbers
            ],
            [
              RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_FREQUENCY,
              intervention.frequency
            ]
          ])
        }))

      planOfCareAnswers.push({
        linkId:
          RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO,
        value: interventionAnswers ?? undefined
      })
    }

    // Build initials authorization flat answers
    if (formData[PlanOfCareFormField.INITIALS_AUTHORIZATION]) {
      planOfCareAnswers.push({
        linkId: RecertAssessmentQuestionnaireItem.PLAN_OF_CARE_INITIALS_AUTHORIZATION,
        value: formData[PlanOfCareFormField.INITIALS_AUTHORIZATION]
      })
    }

    return planOfCareAnswers
  }
}
